import { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
    getMe,
    setViewAsHouseId,
    startImpersonation as startImpersonationAPI,
    stopImpersonation as stopImpersonationAPI,
} from "../services/ledgerService";
import { useAuth } from "./AuthContext";

const STORAGE_KEY = "view_as_house";

const ImpersonationContext = createContext(null);

function loadInitial() {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

export function ImpersonationProvider({ children }) {
    const { isAuthenticated } = useAuth();

    // Phase 1: read-only "view as house" — admin browses another house's
    // data via ?as_house= override. State lives in sessionStorage.
    const [viewingHouse, setViewingHouse] = useState(loadInitial);

    // Phase 2: full impersonation — backend pla cookie carries act claim;
    // frontend learns about it via GET /api/me. `me` is { user, actingAs }.
    const [me, setMe] = useState(null);

    useEffect(() => {
        setViewAsHouseId(viewingHouse ? viewingHouse.houseId : null);
        if (viewingHouse) {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(viewingHouse));
        } else {
            sessionStorage.removeItem(STORAGE_KEY);
        }
    }, [viewingHouse]);

    const refreshMe = useCallback(async () => {
        if (!isAuthenticated) {
            setMe(null);
            return null;
        }
        try {
            const data = await getMe();
            setMe(data);
            return data;
        } catch {
            setMe(null);
            return null;
        }
    }, [isAuthenticated]);

    useEffect(() => {
        refreshMe();
    }, [refreshMe]);

    const startViewing = useCallback((house) => {
        setViewingHouse({
            houseId: house.houseId,
            ownerEmail: house.ownerEmail,
            houseDescription: house.houseDescription,
        });
    }, []);

    const stopViewing = useCallback(() => {
        setViewingHouse(null);
    }, []);

    const startLoginAs = useCallback(async (userId, reason) => {
        await startImpersonationAPI(userId, reason);
        await refreshMe();
    }, [refreshMe]);

    const stopLoginAs = useCallback(async () => {
        await stopImpersonationAPI();
        await refreshMe();
    }, [refreshMe]);

    const actingAs = me?.actingAs || null;

    return (
        <ImpersonationContext.Provider value={{
            // Phase 1
            viewingHouse,
            startViewing,
            stop: stopViewing,
            // Phase 2
            me,
            actingAs,
            startLoginAs,
            stopLoginAs,
            refreshMe,
        }}>
            {children}
        </ImpersonationContext.Provider>
    );
}

export function useImpersonation() {
    const ctx = useContext(ImpersonationContext);
    if (!ctx) {
        throw new Error("useImpersonation must be used within an ImpersonationProvider");
    }
    return ctx;
}
